import { useEffect, useState } from 'react';
import {
  Plus, Trash2, ChevronDown, ChevronUp, AlertTriangle, BookOpen,
  Eye, EyeOff, UserRound, X
} from 'lucide-react';
import { curatedListsApi } from '../../../services/adminApi';
import BookSearchSelect from '../BookSearchSelect';

const emptyForm = () => ({ name: '', description: '', icon: '📋', color: '#A78BFA' });

const CuratedLists = () => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const [expandedId, setExpandedId] = useState(null);
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);

  useEffect(() => {
    loadLists();
  }, []);

  const loadLists = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await curatedListsApi.getLists();
      setLists(result.lists || []);
    } catch (err) {
      setError(err.message || 'Failed to load curated lists');
    } finally {
      setLoading(false);
    }
  };

  const loadItems = async (id) => {
    try {
      setItemsLoading(true);
      const result = await curatedListsApi.getItems(id);
      setItems(result.items || []);
    } catch (err) {
      setError(err.message || 'Failed to load list items');
    } finally {
      setItemsLoading(false);
    }
  };

  const toggleExpand = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      setItems([]);
    } else {
      setExpandedId(id);
      loadItems(id);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.name.trim().length < 2) {
      setError('Name must be at least 2 characters.');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      await curatedListsApi.createList({
        name: form.name.trim(),
        description: form.description.trim() || null,
        icon: form.icon,
        color: form.color
      });
      setForm(emptyForm());
      setShowCreate(false);
      await loadLists();
    } catch (err) {
      setError(err.message || 'Failed to create curated list');
    } finally {
      setSubmitting(false);
    }
  };

  const patchList = async (id, data) => {
    try {
      setSavingId(id);
      setError(null);
      await curatedListsApi.updateList(id, data);
      await loadLists();
    } catch (err) {
      setError(err.message || 'Failed to update list');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (list) => {
    if (!confirm(`Delete curated list "${list.name}"? This also removes its books. This cannot be undone.`)) {
      return;
    }
    try {
      setSavingId(list.id);
      await curatedListsApi.deleteList(list.id);
      if (expandedId === list.id) setExpandedId(null);
      await loadLists();
    } catch (err) {
      setError(err.message || 'Failed to delete list');
    } finally {
      setSavingId(null);
    }
  };

  const handleAddBook = async (id, book) => {
    if (!book) return;
    try {
      setError(null);
      await curatedListsApi.addItem(id, book.id);
      await loadItems(id);
      await loadLists();
    } catch (err) {
      setError(err.message || 'Failed to add book');
    }
  };

  const handleRemoveBook = async (id, itemId) => {
    try {
      await curatedListsApi.removeItem(id, itemId);
      await loadItems(id);
      await loadLists();
    } catch (err) {
      setError(err.message || 'Failed to remove book');
    }
  };

  const toggleHomepage = (list) => patchList(list.id, { homepage_visible: !list.homepage_visible });
  const toggleSpotlight = (list) => patchList(list.id, { is_author_spotlight: !list.is_author_spotlight });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary mb-2">Curated Lists</h1>
          <p className="text-text-secondary">
            Editorial book lists shown on the mobile Home screen. Created here as admin content —
            not tied to any child profile.
          </p>
        </div>
        {!showCreate && (
          <button onClick={() => { setShowCreate(true); setError(null); }} className="btn-primary flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>New List</span>
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex justify-between">
          <span className="flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> {error}</span>
          <button onClick={() => setError(null)} className="font-bold ml-4">×</button>
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <form onSubmit={handleCreate} className="bg-bg-surface rounded-lg border border-gray-200 p-6 space-y-4">
          <h3 className="font-semibold text-text-primary">New curated list</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-text-primary mb-1">Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Cosy Bedtime Reads"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Icon (emoji)</label>
              <input
                type="text"
                value={form.icon}
                onChange={(e) => setForm(prev => ({ ...prev, icon: e.target.value }))}
                maxLength={4}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">Color</label>
              <input
                type="color"
                value={form.color}
                onChange={(e) => setForm(prev => ({ ...prev, color: e.target.value }))}
                className="w-full h-[42px] px-1 py-1 border border-gray-200 rounded-lg"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Optional short description (also used as the author bio when spotlighted)."
              className="w-full border border-gray-200 rounded px-3 py-2 h-20 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>
          <div className="flex space-x-3">
            <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50">
              {submitting ? 'Creating…' : 'Create List'}
            </button>
            <button
              type="button"
              onClick={() => { setShowCreate(false); setForm(emptyForm()); setError(null); }}
              className="px-4 py-2 border border-gray-200 text-text-secondary rounded hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading && (
        <div className="bg-bg-surface rounded-lg border border-gray-200 p-12 text-center">
          <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading curated lists…</p>
        </div>
      )}

      {!loading && lists.length === 0 && (
        <div className="bg-bg-surface rounded-lg border border-gray-200 p-12 text-center text-text-secondary">
          <ListEmptyIcon />
          <h3 className="text-lg font-medium text-text-primary mb-2">No curated lists yet</h3>
          <p>Create your first editorial list above.</p>
        </div>
      )}

      {/* Lists */}
      {!loading && lists.map((list) => (
        <div key={list.id} className="bg-bg-surface rounded-lg border border-gray-200">
          <div className="p-4 flex items-start gap-4">
            <div
              className="w-11 h-11 rounded-lg flex items-center justify-center text-xl flex-shrink-0"
              style={{ backgroundColor: `${list.color}22` }}
            >
              {list.icon || '📋'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-text-primary">{list.name}</h3>
                {list.homepage_visible
                  ? <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">On Home</span>
                  : <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">Hidden</span>}
                {list.is_author_spotlight && (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-primary-100 text-primary-700 inline-flex items-center gap-1">
                    <UserRound className="w-3 h-3" /> Discover Author
                  </span>
                )}
              </div>
              {list.description && <p className="text-sm text-text-secondary mt-1 line-clamp-2">{list.description}</p>}
              <p className="text-xs text-text-secondary mt-1">{list.book_count} {list.book_count === 1 ? 'book' : 'books'}</p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-2">
                <label className="text-xs text-text-secondary">Order</label>
                <input
                  type="number"
                  defaultValue={list.homepage_order ?? ''}
                  onBlur={(e) => {
                    const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
                    if (val !== (list.homepage_order ?? null)) patchList(list.id, { homepage_order: val });
                  }}
                  className="w-16 px-2 py-1 border border-gray-200 rounded text-sm"
                  title="Home row order (lower shows first, max 4 rows)"
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleHomepage(list)}
                  disabled={savingId === list.id}
                  className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50"
                  title={list.homepage_visible ? 'Hide from Home' : 'Show on Home'}
                >
                  {list.homepage_visible ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  <span>{list.homepage_visible ? 'Hide' : 'Show'}</span>
                </button>
                <button
                  onClick={() => toggleSpotlight(list)}
                  disabled={savingId === list.id}
                  className={`flex items-center gap-1 px-2 py-1 text-xs rounded disabled:opacity-50 ${
                    list.is_author_spotlight
                      ? 'bg-primary-500 text-white hover:bg-primary-600'
                      : 'border border-gray-200 hover:bg-gray-50'
                  }`}
                  title="Feature as Discover Author"
                >
                  <UserRound className="w-3.5 h-3.5" />
                  <span>{list.is_author_spotlight ? 'Spotlighted' : 'Spotlight'}</span>
                </button>
                <button
                  onClick={() => handleDelete(list)}
                  disabled={savingId === list.id}
                  className="p-1 text-text-secondary hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                  title="Delete list"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <button
            onClick={() => toggleExpand(list.id)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 border-t border-gray-200 text-sm text-text-secondary hover:bg-gray-50"
          >
            {expandedId === list.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            <span>{expandedId === list.id ? 'Hide books' : 'Manage books'}</span>
          </button>

          {expandedId === list.id && (
            <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-4">
              <BookSearchSelect
                label="Add a book"
                onSelect={(book) => handleAddBook(list.id, book)}
                helperText="Search and select a book to append to this list."
              />

              {itemsLoading ? (
                <p className="text-sm text-text-secondary">Loading books…</p>
              ) : items.length === 0 ? (
                <p className="text-sm text-text-secondary">No books in this list yet.</p>
              ) : (
                <div className="divide-y divide-gray-200 bg-white rounded-lg border border-gray-200">
                  {items.map((item, idx) => (
                    <div key={item.item_id} className="p-3 flex items-center gap-3">
                      <span className="text-xs text-text-secondary w-5">{idx + 1}</span>
                      {item.coverUrl ? (
                        <img src={item.coverUrl} alt={item.title} className="w-8 h-11 object-cover rounded" />
                      ) : (
                        <div className="w-8 h-11 rounded bg-gray-100 flex items-center justify-center">
                          <BookOpen className="w-4 h-4 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-text-primary truncate">{item.title}</div>
                        <div className="text-xs text-text-secondary truncate">{item.author || 'Unknown author'}</div>
                      </div>
                      <button
                        onClick={() => handleRemoveBook(list.id, item.item_id)}
                        className="p-1 text-text-secondary hover:text-red-600 hover:bg-red-50 rounded"
                        title="Remove from list"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const ListEmptyIcon = () => (
  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
    <BookOpen className="w-6 h-6 text-gray-400" />
  </div>
);

export default CuratedLists;
